/**
 * Electrical Drawing Utilities
 *
 * Two jobs, drawn at two different points in the floor-plan pipeline:
 *
 * 1. `drawElectricalDeviceSymbol` / `drawLightSymbol` — the plan symbol for one
 *    switch, outlet, ceiling device or light fixture, in WORLD units inside the
 *    object's own rotated frame. `drawObjectShape` calls them, so devices and
 *    fixtures go through the normal object pipeline in Electrical mode: they
 *    are selectable, highlighted, moved and deleted like any other object.
 * 2. `drawElectricalOverlay` — the wiring (dotted switch → fixture arcs) and,
 *    while the Wires tool is running, a target ring around each fixture the run
 *    can still reach, in SCREEN coordinates on top of everything. It does not
 *    draw device symbols; that would draw every device twice.
 */

import {
  ELECTRICAL_DEVICES,
  SWITCH_TYPES,
  isElectricalDeviceObject,
  isSurfaceElectricalDevice,
  isWallElectricalDevice,
  isWireableFixture,
} from './electricalDevices';
import { lightSymbolOf } from './lightingClassification';
import { apply3DRotationTo2D } from './geometryUtils';

/**
 * Symbol geometry, as multiples of the glyph radius. Matches the tray icons in
 * `AddElementsPanel` (viewBox 24, circle r 8) so the thing you pick from the
 * "+" menu is the thing you see on the plan.
 */
const LIGHT_SYMBOL_RADIUS_PX = 9;
const RECESSED_INNER_FRAC = 0.5; // tray: r 4 inside r 8
const SURFACE_INNER_FRAC = 0.25; // tray: r 2 inside r 8
const SURFACE_TICK_OUTER_FRAC = 1.35; // tray: ticks run from the rim outward
const SCONCE_STEM_FRAC = 1.5;

/**
 * Wire appearance. The bow fraction is shared with the hit test.
 *
 * The bow exists so two wires between nearby fixtures stay tellable apart. At
 * 0.3 it cost far more than it bought: these plans are almost all SHORT hops
 * — on one real structure, 73 wires with a median chord of 1.22 m and only one
 * over 4 m — so every wire departed a third of its own length from the line it
 * should read as, and a chain of evenly spaced cans drew as a scallop instead
 * of a run. A tenth still separates neighbours and reads as a line.
 *
 * Kept a FRACTION rather than a pixel cap on purpose: the control point is
 * computed in screen pixels, so a fixed-pixel bow would shrink against the
 * drawing at print resolution — and print is what these are for.
 */
const WIRE_BOW_FRAC = 0.1;
const WIRE_WIDTH = 2.25;
const WIRE_COLOR = '#FF0000';
const WIRE_SELECTED_COLOR = '#b91c1c';
const WIRE_SELECTED_HALO = 'rgba(249, 115, 22, 0.45)';
/** The grab handle that shapes a wire. Also its hit radius. */
export const WIRE_HANDLE_RADIUS_PX = 6;

/**
 * Draw a device's plan symbol. The context is already translated to the
 * object's center and rotated by `objectAngle`; local +x points away from the
 * wall into the room (the same frame `drawObjectShape` uses for wall lights),
 * so a wall device's symbol grows out of the wall along +x.
 *
 * Symbols are sized in PIXELS (converted by `scale`) — a 7 cm plate is a few
 * pixels wide at plan zoom, and a symbol that small is unreadable. Stroke colour
 * is left as the caller set it, so the selection highlight still shows.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} type - Device type id (see ELECTRICAL_DEVICES)
 * @param {number} scale - Pixels per metre
 * @param {number} [objectAngle=0] - The frame's rotation, for upright text
 */
export const drawElectricalDeviceSymbol = (
  ctx,
  type,
  scale,
  objectAngle = 0
) => {
  const t = String(type || '').toLowerCase();
  const px = (n) => n / scale;
  const r = px(8);
  const ink = ctx.strokeStyle;

  const label = (text, x, y, size = 11, weight = 700) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-objectAngle);
    ctx.fillStyle = ink;
    ctx.font = `${weight} ${px(size)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, 0);
    ctx.restore();
  };

  const circle = (x, y, radius) => {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.stroke();
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.lineWidth = Math.max(ctx.lineWidth, px(1.5));

  if (isWallElectricalDevice(t)) {
    // Short stem from the wall face out to the glyph.
    const stem = px(9);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(stem, 0);
    ctx.stroke();
    const cx = stem + r;

    if (SWITCH_TYPES.includes(t)) {
      // Switch: a dot on the wall and the letter S; dimmer S_D, 3-way S_3.
      ctx.beginPath();
      ctx.arc(0, 0, px(2.5), 0, Math.PI * 2);
      ctx.fillStyle = ink;
      ctx.fill();
      label('S', cx, 0, 13);
      if (t !== 'switch') {
        label(t === 'dimmer' ? 'D' : '3', cx + px(7), px(5), 8);
      }
    } else {
      // Duplex receptacle: a circle crossed by two short lines.
      circle(cx, 0, r);
      ctx.beginPath();
      for (const offset of [-r * 0.35, r * 0.35]) {
        ctx.moveTo(cx - r * 1.5, offset);
        ctx.lineTo(cx + r * 1.5, offset);
      }
      ctx.stroke();
      if (t === 'gfci-outlet') label('GFI', cx + r * 2.9, 0, 8);
      if (t === 'outlet-240v') {
        // Special-purpose receptacle: a filled triangle and its voltage.
        ctx.beginPath();
        ctx.moveTo(cx - r * 0.45, -r * 0.45);
        ctx.lineTo(cx + r * 0.55, 0);
        ctx.lineTo(cx - r * 0.45, r * 0.45);
        ctx.closePath();
        ctx.fillStyle = ink;
        ctx.fill();
        label('240', cx + r * 2.9, 0, 8);
      }
    }
  } else if (isSurfaceElectricalDevice(t)) {
    // Floor receptacle: the duplex glyph ENCLOSED IN A CIRCLE — the standard
    // way a plan marks a receptacle set into the floor rather than on a wall.
    // Drawn centered with no stem, because there is no wall for it to stand
    // off: the stem on a wall device is what carries its symbol out of the
    // wall into the room, and a floor outlet has nothing to stand off from.
    const inner = r * 0.95;
    circle(0, 0, r * 1.7);
    circle(0, 0, inner);
    ctx.beginPath();
    for (const offset of [-r * 0.33, r * 0.33]) {
      ctx.moveTo(-inner, offset);
      ctx.lineTo(inner, offset);
    }
    ctx.stroke();
  } else if (t === 'exhaust-fan') {
    // Exhaust fan: a square grille marked EF.
    const half = r * 1.3;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(-half, -half, half * 2, half * 2);
    ctx.strokeRect(-half, -half, half * 2, half * 2);
    label('EF', 0, 0, 9);
  } else if (t === 'smoke-detector') {
    // Smoke / CO detector: a circle marked SD.
    circle(0, 0, r * 1.2);
    label('SD', 0, 0, 8);
  }

  ctx.restore();
};

/**
 * Draw a light fixture's plan symbol — the standard electrical-plan glyphs,
 * NOT the decorative circle the normal floor plan draws. Same frame and same
 * pixel sizing as `drawElectricalDeviceSymbol`, and likewise solid-line: the
 * dashed "above the cut plane" outline every ceiling fixture otherwise
 * inherits from `isHighObject` is an architectural convention that has no
 * meaning on an electrical drawing.
 *
 * Ceiling symbols are counter-rotated so their ticks stay square to the sheet
 * — an electrical symbol is read upright whatever angle its fixture sits at.
 * The sconce keeps the object's frame, because its stem has to meet the wall.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {'recessed'|'surface'|'sconce'|'fan'} symbol - From `lightSymbolOf`,
 *   or `'fan'` for a ceiling fan (the inspected-electrical layer)
 * @param {number} scale - Pixels per metre
 * @param {number} [objectAngle=0] - The frame's rotation, for upright symbols
 * @param {number} [halfDepth=0] - Half the object's depth, in metres: where a
 *   wall-mounted fixture's wall face sits in the local frame.
 */
export const drawLightSymbol = (
  ctx,
  symbol,
  scale,
  objectAngle = 0,
  halfDepth = 0
) => {
  const px = (n) => n / scale;
  const r = px(LIGHT_SYMBOL_RADIUS_PX);
  const ink = ctx.strokeStyle;

  const circle = (x, y, radius, fill = '#ffffff') => {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.stroke();
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = ink;
  ctx.lineWidth = Math.max(ctx.lineWidth, px(1.5));

  if (symbol === 'sconce') {
    // Circle on a stem, growing out of the wall face into the room. The stem
    // starts at -halfDepth because a wall light's center sits proud of the
    // wall, unlike a device's, which sits on it.
    const stem = r * SCONCE_STEM_FRAC;
    ctx.beginPath();
    ctx.moveTo(-halfDepth, 0);
    ctx.lineTo(-halfDepth + stem, 0);
    ctx.stroke();
    const cx = -halfDepth + stem + r;
    circle(cx, 0, r);
    circle(cx, 0, r * SURFACE_INNER_FRAC, ink);
  } else {
    // Ceiling symbols read upright regardless of the fixture's rotation.
    ctx.rotate(-objectAngle);
    circle(0, 0, r);
    if (symbol === 'recessed') {
      // Downlight can: concentric circles.
      circle(0, 0, r * RECESSED_INNER_FRAC);
    } else if (symbol === 'fan') {
      // Ceiling fan: the ceiling outlet's circle and hub with four blades
      // across it on the diagonals, so it cannot be read as a light.
      ctx.fillStyle = ink;
      for (let k = 0; k < 4; k += 1) {
        const a = Math.PI / 4 + (k * Math.PI) / 2;
        ctx.beginPath();
        ctx.ellipse(
          Math.cos(a) * r * 0.55,
          Math.sin(a) * r * 0.55,
          r * 0.45,
          r * 0.16,
          a,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
      circle(0, 0, r * SURFACE_INNER_FRAC, ink);
    } else {
      // Every other ceiling fixture — pendant, chandelier, flush mount, puck
      // — is one ceiling outlet: circle, hub, and four ticks off the rim.
      circle(0, 0, r * SURFACE_INNER_FRAC, ink);
      const outer = r * SURFACE_TICK_OUTER_FRAC;
      ctx.beginPath();
      for (const [dx, dy] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ]) {
        ctx.moveTo(dx * r, dy * r);
        ctx.lineTo(dx * outer, dy * outer);
      }
      ctx.stroke();
    }
  }

  ctx.restore();
};

/**
 * The symbol an object should be drawn with on the electrical plan, or null
 * when it is not a light. Re-exported so `drawObjectShape` has one import.
 */
export { lightSymbolOf };

/**
 * Where a device's or fixture's SYMBOL actually sits, in world coordinates.
 *
 * A wall device's glyph is not over its object: `drawElectricalDeviceSymbol`
 * runs a stem out of the wall and puts the circle beyond it, so the mark you
 * aim at is ~17 px into the room from the plate. A ceiling symbol is centred.
 *
 * @param {Object} obj - Object with `center` and `rotation`
 * @param {number} scale - Pixels per metre
 * @returns {{x: number, z: number}|null} World position of the glyph's centre
 */
export const symbolWorldCenter = (obj, scale) => {
  if (!obj?.center || !(scale > 0)) return null;
  const { x, z } = obj.center;
  const type = String(obj.type || '').toLowerCase();
  const onWall = isWallElectricalDevice(type);

  // Only wall-mounted marks stand off on a stem. A ceiling device and a SURFACE
  // outlet are both drawn centred — the surface outlet deliberately so, having
  // no wall to stand off from — so their pad is on the object.
  if (!onWall && lightSymbolOf(obj) !== 'sconce') return { x, z };

  // Local +x points out of the wall into the room — the frame drawObjectShape
  // hands the symbol. A sconce's stem starts at the wall face, half a depth
  // behind its centre; a device's centre is already on the face.
  const stemPx = onWall
    ? 9 + 8
    : LIGHT_SYMBOL_RADIUS_PX * (SCONCE_STEM_FRAC + 1);
  const halfDepth = onWall ? 0 : (obj.dimensions?.z || 0) / 2;
  const reach = stemPx / scale - halfDepth;

  // The SAME frame `drawObject` establishes before handing the symbol its
  // context: local +x is `apply3DRotationTo2D(rotation, [0, 0, 1])`, and after
  // `ctx.rotate(objectAngle)` canvas y is world z. Deriving it any other way
  // is how a hit pad ends up on the wrong side of the wall.
  const dir = apply3DRotationTo2D(obj.rotation, [0, 0, 1]);
  const angle = Math.atan2(dir[1], dir[0]);
  return { x: x + Math.cos(angle) * reach, z: z + Math.sin(angle) * reach };
};

/**
 * Screen-pixel radius a device / fixture symbol is clickable within.
 *
 * In PIXELS, deliberately. Hit-testing an electrical object against its real
 * footprint gives a switch plate a 5 x 3 px target at plan zoom — the symbol
 * standing in for it is 16 px across and does not shrink when you zoom out,
 * so neither should what you can hit.
 */
export const SYMBOL_HIT_RADIUS_PX = 15;

/**
 * Is a world point on an electrical object's drawn symbol?
 *
 * @param {{x: number, z: number}} point - World point (z as `z`)
 * @param {Object} obj
 * @param {number} scale - Pixels per metre
 * @param {number} [radiusPx]
 * @returns {boolean}
 */
export const isPointOnElectricalSymbol = (
  point,
  obj,
  scale,
  radiusPx = SYMBOL_HIT_RADIUS_PX
) => {
  const centre = symbolWorldCenter(obj, scale);
  if (!centre || !point) return false;
  const reach = radiusPx / scale;
  return Math.hypot(point.x - centre.x, point.z - centre.z) <= reach;
};

/**
 * The electrical object a click on the plan means, or null.
 *
 * Two rules, both learned the hard way:
 *
 * - **Only things that can take part in wiring are candidates.** Testing every
 *   object gives a sofa, a countertop and a door each a 15 px pad around its
 *   centre, so a near-miss on a switch resolves to furniture and the Wires tool
 *   answers "click a light or exhaust fan" about a coffee table. Filtered, that
 *   message only ever appears for something electrical that genuinely is not a
 *   load — an outlet, a detector — which is when it is actually useful.
 * - **Nearest wins, not first.** Devices are ganged: this corpus has a switch,
 *   two 3-ways and a dimmer within 45 cm of each other, and at plan zoom their
 *   pads all overlap. Taking the first match in array order means clicking the
 *   3-way silently selects the plain switch two positions earlier in the array.
 *
 * @param {Object} params
 * @param {{x: number, z: number}} params.point - Click, in world coordinates
 * @param {Array} params.objects - Candidates (any objects; filtered here)
 * @param {number} params.scale - Pixels per metre
 * @param {number} [params.radiusPx]
 * @returns {Object|null}
 */
export const pickElectricalTargetAt = ({
  point,
  objects,
  scale,
  radiusPx = SYMBOL_HIT_RADIUS_PX,
}) => {
  if (!point || !objects?.length || !(scale > 0)) return null;
  const reach = radiusPx / scale;
  let best = null;
  let bestDistance = Infinity;

  for (const obj of objects) {
    if (!isElectricalDeviceObject(obj) && !isWireableFixture(obj)) continue;
    const centre = symbolWorldCenter(obj, scale);
    if (!centre) continue;
    const distance = Math.hypot(point.x - centre.x, point.z - centre.z);
    if (distance <= reach && distance < bestDistance) {
      bestDistance = distance;
      best = obj;
    }
  }
  return best;
};

/**
 * Draw a wiring TARGET ring — a fixture the running wire can be run to.
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X position in screen coordinates
 * @param {number} y - Y position in screen coordinates
 * @param {number} scale - Current zoom scale
 */
export const drawWiringTarget = (ctx, x, y, scale = 1) => {
  const radius = Math.max(8, Math.min(14, 10 * (scale / 70)));

  ctx.save();

  // Draw outer glow
  ctx.beginPath();
  ctx.arc(x, y, radius + 4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255, 200, 0, 0.3)';
  ctx.fill();

  // Draw inner circle
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw cross pattern for light symbol
  ctx.beginPath();
  ctx.moveTo(x - radius * 0.6, y);
  ctx.lineTo(x + radius * 0.6, y);
  ctx.moveTo(x, y - radius * 0.6);
  ctx.lineTo(x, y + radius * 0.6);
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();
};

/**
 * The control point of the arc a wire is drawn as — bowed perpendicular to the
 * chord so two wires between nearby fixtures stay tellable apart.
 *
 * Exported because the HIT TEST has to measure the same curve the eye sees. A
 * second copy of this arithmetic is how a clickable band drifts off the line
 * it is supposed to belong to.
 *
 * @returns {{x: number, y: number}} The quadratic control point, screen px
 */
export const wireControlPoint = (x1, y1, x2, y2, away = null, shape = null) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy) || 1;
  const nx = -dy / dist;
  const ny = dx / dist;

  // A wire the user has shaped by hand wins outright.
  //
  // The stored pair describes WHERE THE CURVE GOES, not the control point:
  // `along` is a fraction of the chord and `perp` the distance off it in
  // METRES, both read at the curve's midpoint. A control point lies twice as
  // far off the chord as the curve does, so storing that instead would record
  // a number twice what the user dragged — and every reader would have to
  // remember the factor. The doubling happens HERE, once.
  //
  // Chord-relative and in metres, so the shape keeps at any zoom, prints as
  // drawn, and survives a fixture moving a little.
  if (shape?.override && Number.isFinite(shape.scale)) {
    const { along = 0.5, perp = 0 } = shape.override;
    const offsetPx = perp * shape.scale;
    const hx = x1 + dx * along + nx * offsetPx;
    const hy = y1 + dy * along + ny * offsetPx;
    return { x: 2 * hx - (x1 + x2) / 2, y: 2 * hy - (y1 + y2) / 2 };
  }

  const offset = dist * WIRE_BOW_FRAC;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;

  // Which side to bow. A FIXED side made neighbouring runs nest into one
  // another — the opposite of what the bow is for. Bowing away from the run's
  // own anchor separates them: the hops down one side of a room bow one way
  // and those down the other bow the other, so two runs crossing a space do
  // not stack.
  let sign = 1;
  if (away) {
    const toward = (away.x - mx) * nx + (away.y - my) * ny;
    if (toward > 0) sign = -1; // the normal points AT the anchor — flip it
  }

  return { x: mx + nx * offset * sign, y: my + ny * offset * sign };
};

/**
 * The point a run's wires bow AWAY from: the centroid of every endpoint it
 * touches. PURE, and shared with the hit test — the drawn curve and the
 * clickable band must be the same curve, and a second copy of this arithmetic
 * is how they drift apart.
 *
 * @param {Object} run - From `buildWiringRuns`
 * @param {Map} positions - endpoint id → screen `{x, y}`
 * @returns {{x: number, y: number}|null}
 */
export const runBowAnchor = (run, positions) => {
  if (!run?.connections?.length || !positions) return null;
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const c of run.connections) {
    for (const id of [c.switchId, c.fixtureId]) {
      const p = positions.get(id);
      if (p) {
        sx += p.x;
        sy += p.y;
        n += 1;
      }
    }
  }
  return n ? { x: sx / n, y: sy / n } : null;
};

/** anchor for each connection id, so the draw and the hit test agree. */
export const bowAnchorsByConnection = (runs, positions) => {
  const map = new Map();
  for (const run of runs || []) {
    const anchor = runBowAnchor(run, positions);
    if (!anchor) continue;
    for (const c of run.connections || []) map.set(c.id, anchor);
  }
  return map;
};

/**
 * The drawn wire as a polyline, for hit testing and for measuring.
 *
 * @param {number} x1 @param {number} y1 @param {number} x2 @param {number} y2
 * @param {number} [samples=24] - Segments; 24 keeps the chord error well under
 *   a pixel at any length a plan produces.
 * @returns {Array<{x: number, y: number}>}
 */
export const wirePolyline = (
  x1,
  y1,
  x2,
  y2,
  samples = 24,
  away = null,
  shape = null
) => {
  const c = wireControlPoint(x1, y1, x2, y2, away, shape);
  const points = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const d = t * t;
    points.push({
      x: a * x1 + b * c.x + d * x2,
      y: a * y1 + b * c.y + d * y2,
    });
  }
  return points;
};

/**
 * Where the drag handle sits: the MIDPOINT OF THE CURVE.
 *
 * Not the control point — that lies twice as far off the chord as the curve
 * does, so a handle placed there floats away from the line it is shaping and
 * the drag feels like it is pulling on nothing.
 *
 * @returns {{x: number, y: number}}
 */
export const wireHandlePoint = (x1, y1, x2, y2, away = null, shape = null) => {
  const c = wireControlPoint(x1, y1, x2, y2, away, shape);
  return { x: (x1 + 2 * c.x + x2) / 4, y: (y1 + 2 * c.y + y2) / 4 };
};

/**
 * The shape a drag states, from where the handle was dropped. PURE.
 *
 * Simply reads the handle's own position in the chord's frame — a fraction
 * along, and a signed perpendicular distance in METRES — so it is independent
 * of zoom, pan and the print resolution, and reads as the distance the user
 * actually dragged. `wireControlPoint` does the doubling into a control point.
 *
 * @returns {{along: number, perp: number}}
 */
export const wireShapeFromHandle = (x1, y1, x2, y2, handle, scale) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy) || 1;
  const along = ((handle.x - x1) * dx + (handle.y - y1) * dy) / (dist * dist);
  const perp =
    ((handle.x - x1) * -dy + (handle.y - y1) * dx) / dist / (scale || 1);
  return { along, perp };
};

/**
 * Draw the grab handle that shapes a wire, on the curve's midpoint.
 *
 * Only for a SELECTED run: a handle on every wire would be more clutter than
 * the wires are, and the whole point of this is to unclutter.
 */
export const drawWireHandle = (ctx, x, y, { dragging = false } = {}) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, WIRE_HANDLE_RADIUS_PX, 0, Math.PI * 2);
  ctx.fillStyle = dragging ? WIRE_SELECTED_COLOR : '#ffffff';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = WIRE_SELECTED_COLOR;
  ctx.stroke();
  ctx.restore();
};

/**
 * Draw a wiring connection arc between two points
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x1 - Start X (switch position)
 * @param {number} y1 - Start Y (switch position)
 * @param {number} x2 - End X (fixture position)
 * @param {number} y2 - End Y (fixture position)
 * @param {Object} [options]
 * @param {boolean} [options.selected=false] - Part of the selected run
 */
export const drawWiringConnection = (
  ctx,
  x1,
  y1,
  x2,
  y2,
  { selected = false, away = null, shape = null } = {}
) => {
  ctx.save();
  ctx.setLineDash([]);

  const c = wireControlPoint(x1, y1, x2, y2, away, shape);

  if (selected) {
    // A halo under the wire, so a selected run reads as picked at a glance
    // without moving the line itself — the wire stays where it was drawn.
    ctx.strokeStyle = WIRE_SELECTED_HALO;
    ctx.lineWidth = WIRE_WIDTH + 8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.quadraticCurveTo(c.x, c.y, x2, y2);
    ctx.stroke();
  }

  ctx.strokeStyle = selected ? WIRE_SELECTED_COLOR : WIRE_COLOR;
  ctx.lineWidth = selected ? WIRE_WIDTH + 1 : WIRE_WIDTH;

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.quadraticCurveTo(c.x, c.y, x2, y2);
  ctx.stroke();

  ctx.restore();
};

/**
 * Is a wire drawn, given what is currently on screen?
 *
 * EITHER end is enough. A run that starts at a switch in the room you are
 * looking at belongs to that room even though it carries on into the next one,
 * so hiding it because its far fixture is off screen loses the very wire you
 * came to see. The wire is drawn to where that fixture actually is, running
 * out of the room.
 *
 * Shared with the hit test so a wire cannot be drawn-but-unclickable, or
 * clickable-but-invisible.
 *
 * @param {{switchId: string, fixtureId: string}} connection
 * @param {Set<string>|null} visibleIds - Null means everything is on screen
 * @returns {boolean}
 */
export const connectionIsOnScreen = (connection, visibleIds) => {
  if (!visibleIds) return true;
  return (
    visibleIds.has(connection?.switchId) ||
    visibleIds.has(connection?.fixtureId)
  );
};

/**
 * Draw the Electrical-mode overlay: light highlights and the wiring between
 * connected endpoints. Device symbols are drawn by the object pipeline.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} objects - All room objects
 * @param {Object} electricalData - Combined electrical data ({ connections })
 * @param {number} scale - Current zoom scale
 * @param {Object} pan - Current pan offset {x, y}
 * @param {Function} worldToScreen - Function to convert world coords to screen coords
 * @param {Object} [options]
 * @param {boolean} [options.wires=true] - The Wiring toggle: draw the runs
 * @param {boolean} [options.highlightTargets=false] - The Wires tool has a
 *   switch selected: ring every fixture the run can still reach
 * @param {string} [options.startId] - The run's current upstream endpoint
 * @param {Set<string>} [options.selectedConnectionIds] - Hops of the selected
 *   run, drawn picked out
 * @param {Set<string>} [options.visibleIds] - The objects currently ON SCREEN
 *   (a room isolation, a storey, the layer toggles). A wire is drawn when
 *   EITHER end is one of them — a run that starts in the room you are looking
 *   at belongs to that room even though it carries on out of it, so it is
 *   drawn running to where its far fixture actually is. Omit to draw every
 *   wire, which is what the whole-floor view wants.
 * @returns {Map<string, {x: number, y: number}>} Every endpoint's screen
 *   position — the SAME map the wires were drawn from, so a hit test cannot
 *   measure against different geometry than the user sees.
 */
export const drawElectricalOverlay = (
  ctx,
  objects,
  electricalData,
  scale,
  pan,
  worldToScreen,
  {
    wires = true,
    highlightTargets = false,
    startId = null,
    selectedConnectionIds = null,
    visibleIds = null,
    runs = null,
    shapeDrafts = null,
  } = {}
) => {
  if (!objects || objects.length === 0) {
    return new Map();
  }

  const connections = electricalData?.connections || [];
  // A fixture already on a run is wired: its target ring goes away, so what is
  // left ringed is what still needs a wire.
  const wired = new Set();
  connections.forEach((connection) => {
    wired.add(connection.switchId);
    wired.add(connection.fixtureId);
  });

  // Every possible wire endpoint, by id: devices (switches carry the wire
  // start) and wireable fixtures (lights, exhaust fans — the loads). Positions
  // are resolved for EVERY endpoint, on screen or not, so a wire leaving the
  // room still has a real place to run to.
  const endpointPositions = new Map();
  const onScreen = (id) => !visibleIds || visibleIds.has(id);
  objects.forEach((obj) => {
    if (!obj?.center) return;
    const isDevice = isElectricalDeviceObject(obj);
    const isFixture = isWireableFixture(obj);
    if (!isDevice && !isFixture) return;

    const screenPos = worldToScreen(obj.center.x, obj.center.z);
    endpointPositions.set(obj.id, screenPos);

    // Ring the fixtures this run can still reach. Only ones you can see —
    // there is no aiming at a target in a room that is not drawn.
    if (
      highlightTargets &&
      isFixture &&
      !isDevice &&
      obj.id !== startId &&
      !wired.has(obj.id) &&
      onScreen(obj.id)
    ) {
      drawWiringTarget(ctx, screenPos.x, screenPos.y, scale);
    }
  });

  // A connection is drawn only when both endpoints exist — a wire to a fixture
  // that has since been replaced has nothing to land on.
  if (!wires) return { positions: endpointPositions, handles: new Map() };

  // Unselected first, so a selected run's halo is never buried under a wire
  // that crosses it.
  const bowAnchors = bowAnchorsByConnection(runs, endpointPositions);
  // A drag in flight beats what is stored, so the wire follows the pointer.
  const shapeOf = (connection) => {
    const override =
      shapeDrafts?.get(connection.id) || connection.shape || null;
    return override ? { override, scale } : null;
  };
  const handlePositions = new Map();
  const draw = (connection, selected) => {
    const from = endpointPositions.get(connection.switchId);
    const to = endpointPositions.get(connection.fixtureId);
    if (!from || !to) return; // a wire to a replaced fixture lands nowhere
    if (!connectionIsOnScreen(connection, visibleIds)) return;
    const away = bowAnchors.get(connection.id) || null;
    const shape = shapeOf(connection);
    drawWiringConnection(ctx, from.x, from.y, to.x, to.y, {
      selected,
      away,
      shape,
    });
    if (selected) {
      handlePositions.set(
        connection.id,
        wireHandlePoint(from.x, from.y, to.x, to.y, away, shape)
      );
    }
  };
  const isSelected = (connection) =>
    !!selectedConnectionIds?.has(connection.id);
  connections.forEach((c) => !isSelected(c) && draw(c, false));
  connections.forEach((c) => isSelected(c) && draw(c, true));

  // Handles last, so a wire crossing a selected one never paints over its grab
  // point.
  handlePositions.forEach((pt, id) =>
    drawWireHandle(ctx, pt.x, pt.y, { dragging: !!shapeDrafts?.has(id) })
  );

  return { positions: endpointPositions, handles: handlePositions };
};

/**
 * Collect all objects from all rooms in structure data
 * @param {Object} structureData - The structure data with rooms
 * @returns {Array} - Flattened array of all objects with their room context
 */
export const collectAllObjects = (structureData) => {
  if (!structureData?.rooms) return [];

  const allObjects = [];
  structureData.rooms.forEach((room) => {
    if (room.objects) {
      room.objects.forEach((obj) => {
        allObjects.push({
          ...obj,
          roomId: room.id,
          roomElectrical: room.electrical,
        });
      });
    }
  });

  return allObjects;
};

/**
 * Collect all electrical connection data from all rooms
 * @param {Object} structureData - The structure data with rooms
 * @returns {Object} - Combined electrical data with all connections
 */
export const collectAllElectricalData = (structureData) => {
  if (!structureData?.rooms) return { connections: [] };

  const allConnections = [];
  structureData.rooms.forEach((room) => {
    if (room.electrical?.connections) {
      allConnections.push(...room.electrical.connections);
    }
  });

  return { connections: allConnections };
};
